import debounce from 'lodash.debounce'

import {
  LoggerConfig,
  LoggerErrorProps,
  LoggerInfoProps,
  LoggerWarnProps,
  LogParams,
} from './types'

/**
 * Logger utils
 */
class Logger {
  /**
   * The API URL to post logs to
   */
  private static url = ''

  /**
   * Parameters that get sent along with the logs.
   * @see configureLoggerParams to set value.
   */
  private static params: LogParams = {
    client: 'PINE_LABS_KYC_WEB_CLIENT',
    version: '0.0.1',
  }

  private static config = {
    debounceTimeInMs: 10000,
    maxStackSize: 20,
  }

  private static clientInfo = {
    userAgent: navigator.userAgent,
    cookieEnabled: navigator.cookieEnabled,
  }

  /**
   * Helper method to maintain queue and batch logs.
   *
   * @param {function} fn This method runs after debounce
   * @param {number} wait Debounce (delay) time in milliseconds
   * @param {Object} debounceOptions
   * @param {boolean} debounceOptions.maxSize The maximum queue size before it's invoked.
   * @param {boolean} debounceOptions.leading Specify invoking on the leading edge of the timeout.
   * @param {number} debounceOptions.maxWait The maximum time func is allowed to be delayed before it's invoked.
   * @param {boolean} debounceOptions.trailing Specify invoking on the trailing edge of the timeout.
   * @returns the new debounced function.
   */
  private static debounceWithQueue = (
    fn: (args: unknown[]) => void,
    wait: number,
    { maxSize = Infinity, ...debounceOptions } = {},
  ) => {
    const args: unknown[] = []

    const runFn = () => {
      fn(args)
      args.length = 0
    }

    const debounced = debounce(runFn, wait, debounceOptions)

    const cancel = () => {
      debounced.cancel()
      args.length = 0
    }

    const queuedDebounce = (...a: unknown[]) => {
      args.push(a)
      if (args.length >= maxSize) debounced.flush()
      else debounced()
    }

    /**
     * Cancel delayed func invocations
     */
    queuedDebounce.cancel = cancel

    /**
     * Immediately invoke
     */
    queuedDebounce.flush = debounced.flush

    return queuedDebounce
  }

  /**
   * Sets Logger configurations:
   *
   * - URL
   * - Logger config: debounceTimeInMs, maxStackSize
   *
   * @param {*} options
   * @param {String} options.url Logger URL
   * @param {Object} options.debounce Debounce time in milliseconds
   * @param {Object} options.maxStackSize Max number of logs to accumulate before posting
   */
  static initializeLogger = ({ url, debounce, maxStackSize, client, version }: LoggerConfig) => {
    if (url) {
      Logger.url = url
    }
    if (debounce) {
      Logger.config.debounceTimeInMs = debounce
    }
    if (maxStackSize) {
      Logger.config.maxStackSize = maxStackSize
    }
    if (client) {
      Logger.params.client = client
    }
    if (version) {
      Logger.params.version = version
    }
  }

  /**
   * Set parameters that get sent along with the logs.
   *
   * @param {*} params.userId Identifier of the logged in user
   * @param {*} params.userRole Role of the logged in user
   */
  static configureLoggerParams = (params: LogParams) => {
    if (params) {
      Logger.params = { ...Logger.params, ...params }
    }
  }

  /**
   * Posts logs in the queue to the Logger API using a HTTP request.
   */
  private static postLogs = async (logQueue: unknown[]) => {
    const payload = logQueue.flat()
    fetch(Logger.url, {
      method: 'POST',
      headers: { 'Content-type': 'application/json', Accept: '*/*' },
      body: JSON.stringify(payload),
    }).catch((e) => {
      console.error(`Error posting logs: ${e}`)
    })
  }

  /**
   * Adds the log entry to the queue.
   */
  static addLogs = Logger.debounceWithQueue(Logger.postLogs, Logger.config.debounceTimeInMs, {
    maxSize: Logger.config.maxStackSize,
  })

  /**
   * Log warnings
   *
   * @param {object} o
   * @param {String} o.message Message to log
   * @param {object} o.options Metadata to log
   */
  static warn({ message, options }: LoggerWarnProps) {
    Logger.addLogs({
      logLevel: 'WARN',
      message,
      params: {
        ...Logger.params,
        timestamp: new Date().toISOString(),
        ...options,
      },
      deviceInfo: Logger.clientInfo,
    })
  }

  /**
   * Log errors
   *
   * @param {object} o
   * @param {String} o.message Message to log
   * @param {object} o.options Metadata to log
   * @param {*} o.error Error to log
   */
  static error({ message, error, options = {} }: LoggerErrorProps) {
    Logger.addLogs({
      logLevel: 'ERROR',
      message,
      params: {
        ...Logger.params,
        timestamp: new Date().toISOString(),
        ...options,
      },
      deviceInfo: Logger.clientInfo,
      exception: {
        name: error?.name,
        message: error?.message || 'Unknown Error',
        // stack: error?.stack, // TODO: fix the error thrown by the API when sending this.
      },
    })
  }

  /**
   * Log information
   *
   * @param {object} o
   * @param {String} o.message Message to log
   * @param {object} o.options Metadata to log
   */
  static info({ message, options = {} }: LoggerInfoProps) {
    Logger.addLogs({
      logLevel: 'INFO',
      message,
      params: {
        ...Logger.params,
        timestamp: new Date().toISOString(),
        ...options,
      },
      deviceInfo: Logger.clientInfo,
    })
  }
}

/**
 * @deprecated
 * @see Logger This utility will be used for logging instead.
 */
export const consoleLog = (logMessage: string) => {
  if (process.env.REACT_APP_ENABLE_LOGGING === 'true') {
    console.log(logMessage)
  }
}

export default {
  initializeLogger: Logger.initializeLogger,
  configureLoggerParams: Logger.configureLoggerParams,
  logInfo: Logger.info,
  logWarning: Logger.warn,
  logError: Logger.error,
  flushLogs: Logger.addLogs.flush,
}
